// Script.js
// Purpose: Manages the list of user domains and scripts, including adding, deleting, and validating domains.
// Logic: Uses React hooks and state to handle domain actions, modals, loaders, and integrates with socket service for script checks.

import axios from "axios";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Tooltip,
} from "flowbite-react";
import { Tooltip as TooltipOld } from "react-tooltip";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SuccessModal from "../components/SuccessModal";
import FailureModal from "../components/FailureModal";
import DeletePopup from "../components/DeletePopup";
import Loader from "../components/Loader";
import { socketService } from "../components/socketService";
import TestScriptInfoPopup from "../components/TestScriptInfoPopup";
import { clog, hasMenuAccess, isUserOwnerOfWorkspace } from "../lib/utils";
import UpgradeModal from "../components/UpgradeModal";

function Script() {
  // Check if user is owner of workspace
  const isOwner = isUserOwnerOfWorkspace();
  const [openDomainModal, setOpenDomainModal] = useState(false);
  const [domainList, setdomainList] = useState({});
  const [website, setwebsite] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [domainLoading, setdomainLoading] = useState(false);
  const [domainadding, setdomainadding] = useState(false);
  const [domainError, setdomainError] = useState(false);
  const [openSuccessModal, setopenSuccessModal] = useState(undefined);
  const [openFailureModal, setopenFailureModal] = useState(undefined);
  const [successFailureMessage, setsuccessFailureMessage] = useState("");
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [selectedDomain, setselectedDomain] = useState({});
  const [deleting, setdeleting] = useState(false);
  const [domainId, setdomainId] = useState("");
  const [activeToasts, setActiveToasts] = useState(new Set());
  const [scriptCheckInfoPopup, setscriptCheckInfoPopup] = useState(false);
  // State and handlers for "Add Specific Pages" modal
  const [openSpecificPagesModal, setOpenSpecificPagesModal] = useState(false);
  const [specificPages, setSpecificPages] = useState([]);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const [pageInputs, setPageInputs] = useState([
    { id: 1, value: "", isValid: true, errorMessage: "" },
  ]); // Array of page inputs
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    open: false,
    inputId: null,
  });
  const [isSavingPages, setIsSavingPages] = useState(false);
  // State and handlers for track url deletion
  const [deleteTrackUrlConfirmModal, setDeleteTrackUrlConfirmModal] = useState({
    open: false,
    url: "",
  });
  const [isDeletingTrackUrl, setIsDeletingTrackUrl] = useState(false);
  const navigate = useNavigate();
  // Function to fetch the list of domains
  const getDomainList = async () => {
    setdomainLoading(true);
    try {
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/domain`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        params: {
          workspace_id: selectedWorkspace?.workspace_id || null,
        },
      });
      if (!response.data.error) {
        setdomainList(
          response?.data?.data
            ? {
                ...response.data.data,
                is_knowledgebase: response.data.is_knowledgebase,
              }
            : {}
        );
      } else {
        clog("Error in fetching domain");
        setdomainList({});
        //show failure modal
        setsuccessFailureMessage(response?.data?.message);
        setopenFailureModal("dismissible");
      }
      setdomainLoading(false);
    } catch (error) {
      clog("Error in fetching domain", error);
      setdomainList({});
      if (error?.response?.status == 401 || error?.response?.status == 403) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      setdomainLoading(false);
      setsuccessFailureMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch domain"
      );
      setopenFailureModal("dismissible");
    }
  };
  clog("domainList", domainList);
  // Function to add a new domain
  const addDomain = async () => {
    if (!isOwner) {
      notify("Only workspace owner can perform this action", "error");
      return;
    }
    setdomainadding(true);
    try {
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/domain/add`,
        method: "POST",
        data: {
          domain: website,
          is_primary: 1,
          plan_id: JSON.parse(localStorage.getItem("login_data"))?.resp_user
            ?.plan_id,
          subscription_id: JSON.parse(localStorage.getItem("login_data"))
            ?.resp_user?.subscription_id,
          workspace_id: selectedWorkspace?.workspace_id || null,
        },
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data.error) {
        setdomainadding(false);
        setOpenDomainModal(false);
        setwebsite("");
        getDomainList();
        setTimeout(() => {
          notify(response?.data?.message);
        }, 200);
      } else {
        clog("Error in fetching notifications");
        setdomainadding(false);
        setsuccessFailureMessage(response?.message);
        setopenFailureModal("dismissible");
      }
    } catch (error) {
      clog("Error in fetching notifications", error);
      setdomainadding(false);
      setopenFailureModal("dismissible");
      setsuccessFailureMessage(
        error?.response?.data?.message || error?.message
      );
    }
  };
  // Function to handle the delete confirmation modal
  const closeModalHandlerDelete = () => {
    setOpenModalDelete(undefined);
  };
  // Function to open the delete confirmation modal
  const deleteFile = async () => {
    if (!isOwner) {
      notify("Only workspace owner can perform this action", "error");
      return;
    }
    setdeleting(true);
    try {
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/domain/delete`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          id: domainList?.id,
          workspace_id: selectedWorkspace?.workspace_id || null,
        },
      });
      setdeleting(false);
      closeModalHandlerDelete();
      if (!response?.error) {
        setsuccessFailureMessage(response?.message);
        setopenSuccessModal("dismissible");
        setTimeout(() => {
          getDomainList();
        }, 200);
      } else {
        setsuccessFailureMessage(response?.message);
        setopenFailureModal("dismissible");
      }
    } catch (error) {
      clog("Error in fetching notifications", error);
      setdeleting(false);
      setopenFailureModal("dismissible");
      setsuccessFailureMessage(
        error?.response?.data?.message || error?.message
      );
    }
  };
  const removeModal = () => {
    setopenSuccessModal(undefined);
    setopenFailureModal(undefined);
  };
  // Function to handle input changes and validate the URL
  const handleChange = (e) => {
    const inputValue = e.target.value.trim();
    // remove http(s):// from the input value
    const formattedValue = inputValue.replace(/^(https?:\/\/)?(www\.)?/, "");
    // set the formatted value back to the state
    setwebsite(formattedValue);
    // set the input value to the state
    // setwebsite(inputValue);

    // const urlPattern =
    //   /^(https?:\/\/)?(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,6}(\/.*)?$/;
    const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;
    setIsValid(urlPattern.test(inputValue));
  };
  // Function to notify the user with a toast message
  const notify = (message, type) => {
    const toastKey = `${message}-${type}`;
    if (activeToasts.has(toastKey)) {
      return;
    }

    setActiveToasts((prev) => new Set([...prev, toastKey]));
    toast(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      type: type || "default",
      onClose: () => {
        setActiveToasts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(toastKey);
          return newSet;
        });
      },
    });
  };
  // const scriptCheckFn = () => {
  //   let scriptDetected = false;
  //   let socketListenerAdded = false;

  //   let newwindow = window.open(
  //     `https://${domainList?.domain}?uid=${domainList?.uniq_token}`,
  //     Date.now(),
  //     "height=500,width=500"
  //   );

  //   if (window.focus) {
  //     newwindow.focus();
  //   }

  //   // Remove any existing listeners first
  //   socketService.off("website_script");

  //   const timeoutDuration = 5000;
  //   const scriptCheckTimeout = setTimeout(() => {
  //     if (!scriptDetected) {
  //       notify(
  //         "Script not detected on website. Please verify installation.",
  //         "error"
  //       );
  //       newwindow.close();
  //       if (socketListenerAdded) {
  //         socketService.off("website_script");
  //       }
  //     }
  //   }, timeoutDuration);

  //   // Setup socket listener before window loads
  //   socketListenerAdded = true;
  //   socketService.on("website_script", (data) => {
  //     if (scriptDetected) return; // Prevent multiple executions

  //     scriptDetected = true;
  //     clearTimeout(scriptCheckTimeout);

  //     clog("Socket connected for website_script", data);
  //     if (data?.type == "website_script" && data?.status == 2) {
  //       notify(data?.contents || "Script added successfully", "success");
  //       newwindow.close();
  //     } else if (data?.type == "website_script" && data?.status == 1) {
  //       notify(data?.contents || "Script not loaded.", "error");
  //       newwindow.close();
  //     }
  //   });

  //   newwindow.onclose = () => {
  //     clearTimeout(scriptCheckTimeout);
  //     clog("New window closed");
  //     if (socketListenerAdded) {
  //       socketService.off("website_script");
  //     }
  //   };
  // };
  // Function to check the script on the website
  const scriptCheckFn = () => {
    let scriptDetected = false;
    let socketListenerAdded = false;
    let timeoutId = null;

    let newwindow = window.open(
      `https://${domainList?.domain}?uid=${
        domainList?.uniq_token
      }&login_user_id=${
        JSON?.parse(localStorage?.getItem("login_data"))?.resp_user?.id
      }`,
      Date.now(),
      "height=500,width=500"
    );

    if (window.focus) {
      newwindow.focus();
    }

    // Remove any existing listeners first
    socketService.off("website_script");

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (socketListenerAdded) {
        socketService.off("website_script");
      }
      clog("Cleanup completed");
    };

    // Check if window was closed
    const checkWindow = setInterval(() => {
      if (newwindow.closed) {
        cleanup();
        clearInterval(checkWindow);
        clog("Window was closed by user");
      }
    }, 1000);

    timeoutId = setTimeout(() => {
      if (!scriptDetected) {
        notify(
          "We couldn't locate the script on your website. Please verify that it has been installed correctly.",
          "error"
        );
        unverifyDomain();
        newwindow.close();
        cleanup();
        clearInterval(checkWindow);
      }
    }, 30000);

    // Setup socket listener before window loads
    socketListenerAdded = true;
    socketService.on("website_script", (data) => {
      if (scriptDetected) return; // Prevent multiple executions

      scriptDetected = true;
      cleanup();
      clearInterval(checkWindow);

      clog("Socket connected for website_script", data);
      if (data?.type == "website_script" && data?.status == 2) {
        notify(data?.contents || "Script added successfully", "success");
        newwindow.close();
      } else if (data?.type == "website_script" && data?.status == 1) {
        notify(data?.contents || "Script not loaded.", "error");
        newwindow.close();
      }
      getDomainList(); // Refresh domain list after script check
    });

    newwindow.onbeforeunload = () => {
      cleanup();
      clearInterval(checkWindow);
    };
  };

  const unverifyDomain = async () => {
    try {
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/domain/unverify`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          id: domainList?.id,
          workspace_id: selectedWorkspace?.workspace_id || null,
        },
      });
      if (!response.data.error) {
        notify(response.data.message || "Domain Status Updated", "success");
      } else {
        notify(
          response.data.message || "Failed to update domain status",
          "error"
        );
      }
    } catch (error) {
      clog("Error updating domain status", error);
    } finally {
      getDomainList(); // Refresh domain list after script check
    }
  };
  // Effect to fetch initial domain list when component mounts
  useEffect(() => {
    getDomainList();

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("open_upgrade") === "true") {
      clog("Open upgrade modal got called");
      setIsUpgradeModalOpen(true);
      localStorage.setItem("open_upgrade", "true");
    }
    // remove the param from url
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);
  // Effect to initialize socket service and handle notifications
  useEffect(() => {
    if (!socketService.isConnected()) {
      socketService.initialize();
    }
    // clog("Socket connected for website_script");
    // socketService.on("website_script", (data) => {
    //   clog('Socket connected for website_script',data);
    // })

    return () => {
      socketService.off("website_script", () => {
        clog("Socket disconnected");
      });
    };
  }, []);

  const handlePageInputChange = (id, value) => {
    setPageInputs((prev) =>
      prev.map((input) =>
        input.id === id
          ? { ...input, value: value.trim(), isValid: true, errorMessage: "" }
          : input
      )
    );
  };

  const validatePageInput = (id, value) => {
    if (!value.trim()) return;

    const domainUrl = domainList?.domain;
    if (!domainUrl) return;
    // Check for duplicate URLs
    const duplicate = pageInputs.some(
      (input) => input.id !== id && input.value.trim() === value.trim()
    );
    if (duplicate) {
      setPageInputs((prev) =>
        prev.map((input) =>
          input.id === id
            ? { ...input, isValid: false, errorMessage: "URL already added" }
            : input
        )
      );
      return;
    }
    // Check against already saved track URLs
    if (domainList.track_urls?.includes(value.trim())) {
      setPageInputs((prev) =>
        prev.map((input) =>
          input.id === id
            ? { ...input, isValid: false, errorMessage: "URL already saved" }
            : input
        )
      );
      return;
    }

    // Extract domain from input URL
    try {
      let inputUrl = value.trim();
      if (!inputUrl.startsWith("http://") && !inputUrl.startsWith("https://")) {
        inputUrl = "https://" + inputUrl;
      }

      const inputDomain = new URL(inputUrl).hostname.replace("www.", "");
      const baseDomain = domainUrl.replace("www.", "");

      if (inputDomain !== baseDomain) {
        setPageInputs((prev) =>
          prev.map((input) =>
            input.id === id
              ? {
                  ...input,
                  isValid: false,
                  errorMessage: `URL must belong to the domain: ${domainUrl}`,
                }
              : input
          )
        );
      }
    } catch (error) {
      setPageInputs((prev) =>
        prev.map((input) =>
          input.id === id
            ? {
                ...input,
                isValid: false,
                errorMessage: "Please enter a valid URL",
              }
            : input
        )
      );
    }
  };

  const addMorePageInput = () => {
    const newId = Math.max(...pageInputs.map((input) => input.id)) + 1;
    setPageInputs((prev) => [
      ...prev,
      { id: newId, value: "", isValid: true, errorMessage: "" },
    ]);
  };

  const removePageInput = (id) => {
    if (pageInputs.length > 1) {
      setDeleteConfirmModal({ open: true, inputId: id });
    }
  };

  const confirmRemovePageInput = () => {
    const { inputId } = deleteConfirmModal;
    setPageInputs((prev) => prev.filter((input) => input.id !== inputId));
    setDeleteConfirmModal({ open: false, inputId: null });
  };

  const isAllInputsValid = () => {
    return pageInputs.every(
      (input) => input.isValid && input.value.trim() !== ""
    );
  };

  const saveSpecificPages = async () => {
    if (!isOwner) {
      notify("Only workspace owner can perform this action", "error");
      return;
    }
    if (!isAllInputsValid()) return;

    setIsSavingPages(true);
    try {
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );
      const validPages = pageInputs
        .filter((input) => input.value.trim() !== "")
        .map((input) => input.value.trim());

      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/domain/track_urls`,
        method: "POST",
        data: {
          // domain: `https://${domainList?.domain}`,
          domain: domainList?.domain,
          track_urls: validPages,
          workspace_id: selectedWorkspace?.workspace_id || null,
        },
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });

      if (!response.data.error) {
        setSpecificPages(validPages);
        setOpenSpecificPagesModal(false);
        setPageInputs([{ id: 1, value: "", isValid: true, errorMessage: "" }]);
        getDomainList();
        notify(
          response?.data?.message || "Pages saved successfully",
          "success"
        );
      } else {
        notify(response?.data?.message || "Failed to save pages", "error");
      }
    } catch (error) {
      clog("Error saving specific pages", error);
      notify(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save pages",
        "error"
      );
    } finally {
      setIsSavingPages(false);
    }
  };
  // Function to confirm and delete a tracked URL
  const confirmDeleteTrackUrl = async () => {
    if (!isOwner) {
      notify("Only workspace owner can perform this action", "error");
      return;
    }
    setIsDeletingTrackUrl(true);
    try {
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/domain/track_urls/delete`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        data: {
          domain: domainList?.domain,
          url: deleteTrackUrlConfirmModal.url,
          workspace_id: selectedWorkspace?.workspace_id || null,
        },
      });
      if (!response.data.error) {
        notify(response.data.message || "Page URL deleted", "success");
        setDeleteTrackUrlConfirmModal({ open: false, url: "" });
        getDomainList();
      } else {
        notify(response.data.message || "Failed to delete URL", "error");
      }
    } catch (error) {
      notify(error?.response?.data?.message || error.message, "error");
    } finally {
      setIsDeletingTrackUrl(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-[24px] font-[600] pb-4">
            <span onClick={() => {}} className="cursor-pointer">
              Domains
            </span>
          </h4>
          <p>
            Add your website domain, install and verify the script to start
            identifying anonymous website visitors.
          </p>
        </div>
        <button
          data-tooltip-id="my-tooltip"
          data-tooltip-content={
            Object.keys(domainList || {}).length === 0
              ? "Please add your domain to get the tracking pixel"
              : ""
          }
          data-tooltip-place="bottom"
          disabled={Object.keys(domainList || {}).length === 0}
          type="button"
          className="flex justify-center items-center py-2 px-[60px] rounded-[20px] font-semibold ring-[1px] ring-[#2A2A2A] ring-inset hover:ring-[none] bg-gradient-to-r hover:from-[#5E78F4] hover:to-[#F14CE2] hover:text-white hover:transition hover:delay-75 disabled:fade-50 disabled:cursor-not-allowed"
          onClick={() => {
            navigate("/pixel-details", {
              state: {
                domainList: domainList,
              },
            });
          }}
        >
          <span className=" text-[18px]">{"</>"} View Script</span>
        </button>
      </div>
      {domainLoading && (
        <>
          {/* Domain Card Skeleton */}
          <div className="flex justify-between items-center border rounded-[20px] px-4 py-4 shadow-md bg-white mb-4 mt-8">
            <div className="flex items-center w-full">
              <div className="w-8 h-8 bg-gray-300 rounded-[6px] skeleton-wave me-6"></div>
              <div className="flex flex-col gap-2 flex-1">
                <div className="h-6 w-40 bg-gray-300 rounded skeleton-wave"></div>
                <div className="h-6 w-56 bg-gray-300 rounded skeleton-wave"></div>
              </div>
              <div className="flex gap-2 items-center ms-auto">
                <div className="h-10 w-36 bg-gray-300 rounded-full skeleton-wave"></div>
                <div className="w-6 h-6 bg-gray-300 rounded skeleton-wave"></div>
              </div>
            </div>
          </div>

          {/* Info Banner Skeleton */}
          <div className="w-full h-16 bg-gray-300 rounded-[10px] skeleton-wave mb-4"></div>

          {/* Add Domain Button Skeleton */}
          <div className="flex justify-end w-full mt-8">
            <div className="h-11 w-48 bg-gray-300 rounded-[20px] skeleton-wave"></div>
          </div>

          {/* Specific Pages Info Banner Skeleton */}
          <div className="w-full h-20 bg-gray-300 rounded-[10px] skeleton-wave mt-8 mb-8"></div>

          {/* Specific Pages Title Skeleton */}
          <div className="h-7 w-64 bg-gray-300 rounded skeleton-wave mb-8"></div>

          {/* Specific Pages List Skeleton */}
          <div className="flex flex-col gap-5 mb-8">
            {[1, 2, 3].map((index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1 h-16 bg-gray-300 rounded-[30px] skeleton-wave"></div>
                <div className="w-6 h-6 bg-gray-300 rounded skeleton-wave"></div>
              </div>
            ))}
          </div>

          {/* Add Specific Pages Button Skeleton */}
          <div className="flex justify-end w-full mt-8 mb-8">
            <div className="h-11 w-52 bg-gray-300 rounded-[20px] skeleton-wave"></div>
          </div>
        </>
      )}
      {!domainLoading && Object.keys(domainList || {}).length === 0 && (
        <div className="flex justify-between items-center border rounded-[20px] px-4 py-4 shadow-md bg-white mb-4 mt-8 cursor-pointer">
          {/* Left Side: Text and File Info */}
          <div className="flex items-center cursor-pointer w-full">
            <div className="flex items-center w-full">
              <div className="bg-[#F0F6FF] w-8 h-8 flex items-center justify-center rounded-[6px] me-6">
                <svg className="icon text-[14px]">
                  <use href="#icon_active_domain"></use>
                </svg>
              </div>

              <div className="flex flex-col">
                <h5 className="text-[18px] font-[600]">Primary domain</h5>
              </div>

              {/* Right Side: Delete Button */}
              <div
                className="flex items-center dot-btn ms-auto"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="flex gap-2 items-center">
                  <button
                    className={`py-2 px-5 w-full  flex justify-center items-center text-center rounded-[20px] text-[18px] font-semibold ring-[1px] ring-[#2A2A2A] bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white hover:transition hover:delay-75 ${
                      !isOwner ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={() => isOwner && setOpenDomainModal(true)}
                    disabled={!isOwner}
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content={
                      !isOwner
                        ? "Only workspace owner can perform this action"
                        : ""
                    }
                    data-tooltip-place="top"
                  >
                    <span>+ Add New Domain</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {!domainLoading && Object.keys(domainList || {}).length > 0 && (
        <div className="flex justify-between items-center border rounded-[20px] px-4 py-4 shadow-md bg-white mb-4 mt-8 cursor-pointer">
          {/* Left Side: Text and File Info */}
          <div className="flex items-center cursor-pointer w-full">
            <div className="flex items-center w-full">
              <div className="bg-[#F0F6FF] w-8 h-8 flex items-center justify-center rounded-[6px] me-6">
                <svg className="icon text-[14px]">
                  <use href="#icon_active_domain"></use>
                </svg>
              </div>

              <div className="flex flex-col">
                {domainList?.is_primary === 1 && (
                  <h5 className="text-[18px] font-[600]">Primary domain</h5>
                )}
                <h5 className="text-[18px] font-[600]">
                  {domainList?.domain}
                  {domainList?.is_verified === 1 ? (
                    <span className="text-[#3BB82A]">(verified)</span>
                  ) : (
                    <Tooltip
                      content="Please verify your domain to start tracking anonymous website visitors."
                      placement="right"
                      trigger="hover"
                      style="light"
                    >
                      <span className="text-[#DF3939]">
                        (unverified)
                        <svg
                          className="icon text-[16px] cursor-pointer hover:text-[#097BA9]"
                          width="16"
                          height="16"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <use href="#icon_eye"></use>
                        </svg>
                      </span>
                    </Tooltip>
                  )}
                </h5>
              </div>

              {/* Right Side: Delete Button */}
              <div
                className="flex items-center dot-btn ms-auto"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="flex gap-2 items-center">
                  <button
                    type="default"
                    onClick={() => {
                      setscriptCheckInfoPopup(true);
                    }}
                    className="py-2 px-6 max-w-[180px] w-full rounded-full font-semibold text-[18px] ring-[1px] ring-[#2A2A2A] ring-inset hover:ring-[none] bg-gradient-to-r hover:from-[#5E78F4] hover:to-[#F14CE2] hover:text-white hover:transition hover:delay-75 flex align-center justify-center"
                  >
                    <svg className="icon text-[20px] mt-[3px] me-2">
                      <use href="#icon_globe"></use>
                    </svg>
                    Test Script
                  </button>
                </div>
                { 

                // hide the delete button if the domain is verified
                domainList?.is_verified != 1 && 
                  <button
                  onClick={(e) => {
                    if (!isOwner) {
                      e.preventDefault();
                      e.stopPropagation();
                      notify(
                        "Only workspace owner can perform this action",
                        "error"
                      );
                      return;
                    }
                    clog("Edit");
                    e.stopPropagation();
                    setOpenModalDelete("dismissible");
                  }}
                  disabled={!isOwner}
                  className={!isOwner ? "opacity-50 cursor-not-allowed" : ""}
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content={
                    !isOwner
                      ? "Only workspace owner can perform this action"
                      : ""
                  }
                  data-tooltip-place="top"
                >
                  <svg className="icon text-[22px] text-[#DF3939] ms-8">
                    <use href="#icon_script_delete"></use>
                  </svg>
                </button>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* <div className="flex justify-between items-center border rounded-[20px] px-4 py-8 shadow-md bg-white mb-8 mt-8 cursor-pointer">
     
      <div className="flex items-center cursor-pointer w-full">
          <div className="flex items-center w-full">
            <div className="bg-[#F0F6FF] w-8 h-8 flex items-center justify-center rounded-[6px] me-6">
              <svg className="icon text-[14px]">
                <use href="#icon_inactive_domain"></use>
              </svg>
            </div>

            <div className="flex flex-col">
              <h5 className="text-[18px] font-[600]">Inactive domains</h5>
            </div>

            <div className="  ms-auto me-8">No inactive domain</div>
          </div>
        </div>
      </div> */}
      {!domainLoading && (
        <div class="text-black w-full text-[16px] font-[500] inline-flex  items-center p-3 rounded-[10px] bg-[#FAFBFC] border border-[#CADFFF]">
          <svg className="icon text-[22px] text-[#DF3939] me-3">
            <use href="#icon_i"></use>
          </svg>
          <div>

         
          One active domain per account. For multiple business domains, write to{" "}
          <a href="mailto:sales@vison.ai" className="text-[#F14CE2]">
            sales@vison.ai
          </a>.
          {
            domainList?.is_verified == 1 && (
              <> 
              <br/>
              <span>
                To delete an active domain, please contact support at <a href="mailto:support@vison.ai" className="text-[#F14CE2]">support@vison.ai</a> or use the in-app chat.
              </span>
              </>
            )
          }
           </div>
        </div>
      )}

      {!domainLoading && Object.keys(domainList || {}).length > 0 && (
        // && !domainList?.is_verified === 1
        <div className="mx-auto flex justify-end w-full mt-8">
          <button
            className={`py-2 w-full max-w-[190px] flex justify-center items-center text-center rounded-[20px] text-[18px] font-semibold ring-[1px] ring-[#2A2A2A] bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white hover:transition hover:delay-75 ${
              !isOwner || Object.keys(domainList || {}).length > 0
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
            onClick={() => isOwner && setOpenDomainModal(true)}
            disabled={Object.keys(domainList || {}).length > 0 || !isOwner}
            data-tooltip-id="my-tooltip"
            data-tooltip-content={
              !isOwner
                ? "Only workspace owner can perform this action"
                : "To add a new domain, please delete the existing one first."
            }
            data-tooltip-place="top"
          >
            <span>+ Add a Domain</span>
          </button>
        </div>
      )}
      {!domainLoading &&
        Object.keys(domainList || {}).length > 0 &&
        domainList?.is_verified === 1 && (
          <>
            <div className="self-stretch w-full mt-8  px-5 py-4 bg-blue-50 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-blue-200 inline-flex  items-center gap-2.5">
              <div className="justify-start text-zinc-800 text-lg font-medium ">
                Please specify the website pages where visitor identity tracking
                should be enabled. If left blank, tracking will be applied to
                all pages, which may result in faster credit consumption.
              </div>
            </div>

            {hasMenuAccess("specific_page_tracking") && (
              <>
                <div className="justify-start text-zinc-800 text-lg font-bold mt-8 mb-8">
                  Specific Pages To Track Identity
                </div>
                <div className="self-stretch flex flex-col justify-start items-start gap-5">
                  {domainList.track_urls?.map((url) => (
                    <div
                      key={url}
                      className="self-stretch inline-flex justify-start items-center gap-2"
                    >
                      <div className="flex-1 pl-px py-px bg-white rounded-[30px] shadow-[0px_3px_4px_0px_rgba(0,0,0,0.06)] outline outline-1 outline-offset-[-1px] outline-neutral-200 flex justify-start items-center gap-7">
                        <div className="min-w-14 min-h-14 px-5 py-5 bg-blue-50 rounded-full outline outline-1 outline-offset-[-1px] outline-gray-50 inline-flex flex-col justify-center items-center">
                          <svg className="icon w-5 text-zinc-800">
                            <use href="#icon_globe"></use>
                          </svg>
                        </div>
                        <div className="text-sky-700 text-lg font-medium">
                          {url}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (!isOwner) {
                            notify(
                              "Only workspace owner can perform this action",
                              "error"
                            );
                            return;
                          }
                          setDeleteTrackUrlConfirmModal({ open: true, url });
                        }}
                        disabled={!isOwner}
                        className={
                          !isOwner ? "opacity-50 cursor-not-allowed" : ""
                        }
                        data-tooltip-id="my-tooltip"
                        data-tooltip-content={
                          !isOwner
                            ? "Only workspace owner can perform this action"
                            : ""
                        }
                        data-tooltip-place="top"
                      >
                        <svg className="icon text-[22px] text-[#DF3939] ms-8">
                          <use href="#icon_script_delete"></use>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mx-auto flex justify-end w-full mt-8 mb-8">
                  <button
                    className={`py-2 w-full max-w-[190px] flex justify-center items-center text-center rounded-[20px] text-[18px] font-semibold ring-[1px] ring-[#2A2A2A] bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white hover:transition hover:delay-75 ${
                      !isOwner ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={() => isOwner && setOpenSpecificPagesModal(true)}
                    disabled={!isOwner}
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content={
                      !isOwner
                        ? "Only workspace owner can perform this action"
                        : ""
                    }
                    data-tooltip-place="top"
                  >
                    <span>+ Add Specific Pages</span>
                  </button>
                </div>
              </>
            )}
            {!hasMenuAccess("specific_page_tracking") && (
              <div className="mx-auto flex justify-end w-full mt-8 mb-8">
                <button
                  className={`py-2 w-full max-w-[190px] flex justify-center items-center text-center rounded-[20px] text-[18px] font-semibold ring-[1px] ring-[#2A2A2A] bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white hover:transition hover:delay-75 ${
                    !isOwner ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  onClick={() => isOwner && setIsUpgradeModalOpen(true)}
                  disabled={!isOwner}
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content={
                    !isOwner
                      ? "Only workspace owner can perform this action"
                      : "Please upgrade your plan to access this feature."
                  }
                  data-tooltip-place="top"
                >
                  <span>+ Add Specific Pages</span>
                </button>
              </div>
            )}
          </>
        )}

      <Modal
        className="domain-popup"
        show={openDomainModal}
        onClose={() => {
          setOpenDomainModal(false);
          setwebsite("");
        }}
      >
        <ModalHeader className="bg-[#FAFBFC]">Add new domain</ModalHeader>
        <ModalBody>
          <div className="space-y-6">
            <div className="my-3 url-input">
              <div className="flex mt-1 rounded-full border border-gray-300 shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-full rounded-r-full border bg-gray-100 text-black text-[18px] font-[600]">
                  <svg className="icon text-[20px] me-2">
                    <use href="#icon_globe"></use>
                  </svg>
                  http(s)://
                </span>
                <input
                  type="text"
                  id="basic-url"
                  value={website}
                  onChange={handleChange}
                  disabled={!isOwner}
                  className="flex-1 block w-full rounded-r-full border-0 py-3 focus:outline-hidden outline-0 outline-offset-0 text-[18px] disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-describedby="basic-addon3 basic-addon4"
                  placeholder="Enter your domain"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              {!isValid && (
                <p className="text-red-500 pt-2">Please enter a valid URL.</p>
              )}
            </div>

            <div className="my-3 bg-[#FAFBFC] rounded-[15px] p-4">
              <h5 className="font-[600] pb-4">Domain formatting rule</h5>

              <div className="flex items-center mb-5">
                <span class="bg-gray-100 text-black text-[18px] font-[600] inline-flex items-center px-3 py-1 me-4 dark:bg-gray-700  border border-black rounded-[10px]">
                  acmeinc.com
                </span>
                <p className="text-[16px] font-[500]">
                  script will load on{" "}
                  <a href="#" className="text-[#6377F3]">
                    acmeinc.com
                  </a>
                  ,{" "}
                  <a href="#" className="text-[#6377F3]">
                    www.acmeinc.com
                  </a>
                  ,{" "}
                  <a href="#" className="text-[#6377F3]">
                    app.acmeinc.com
                  </a>{" "}
                  ,etc.
                </p>
              </div>

              <div className="flex items-center mb-4">
                <span class="bg-gray-100 text-black text-[18px] font-[600] inline-flex items-center px-3 py-1 me-4 dark:bg-gray-700  border border-black rounded-[10px]">
                  www.acmeinc.com
                </span>
                <p className="text-[16px] font-[500]">
                  script will load only on subdomain
                  <a href="#" className="text-[#6377F3] px-2">
                    www.acmeinc.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="border-0">
          <div className="flex items-center justify-end w-full">
            <button
              type="default"
              className="py-2 max-w-[180px] w-full me-6 rounded-[20px] font-semibold text-[18px] ring-[1px] ring-[#2A2A2A] ring-inset hover:ring-[none] bg-gradient-to-r hover:from-[#5E78F4] hover:to-[#F14CE2] hover:text-white hover:transition hover:delay-75"
              onClick={() => {
                setOpenDomainModal(false);
                setwebsite("");
              }}
            >
              Cancel
            </button>

            <button
              type="default"
              className="py-2 max-w-[180px] w-full rounded-[20px] font-semibold text-[18px] ring-[1px] ring-[#2A2A2A] ring-inset hover:ring-[none] bg-gradient-to-r hover:from-[#5E78F4] hover:to-[#F14CE2] hover:text-white hover:transition hover:delay-75 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => addDomain()}
              disabled={!isValid || domainadding || !isOwner}
            >
              {domainadding ? <Loader /> : "Save"}
            </button>
          </div>
        </ModalFooter>
      </Modal>
      <Modal
        show={openSpecificPagesModal}
        onClose={() => {
          setOpenSpecificPagesModal(false);
          setPageInputs([
            { id: 1, value: "", isValid: true, errorMessage: "" },
          ]);
        }}
        className="domain-popup"
      >
        <ModalHeader className="bg-[#FAFBFC]">Add Specific Pages</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div className="flex justify-end sticky top-0">
              <button
                onClick={addMorePageInput}
                disabled={!isOwner}
                className={`flex items-center gap-2 px-3 py-1 bg-[#20C520] text-white rounded-[10px] text-sm font-medium hover:bg-[#1ea01e] transition-colors ${
                  !isOwner ? "opacity-50 cursor-not-allowed" : ""
                }`}
                data-tooltip-id="my-tooltip"
                data-tooltip-content={
                  !isOwner ? "Only workspace owner can perform this action" : ""
                }
                data-tooltip-place="top"
              >
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 1V15M1 8H15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Add More
              </button>
            </div>
            {pageInputs.map((input, index) => (
              <div key={input.id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <div
                    className={`flex-1 pl-px pr-2 py-px bg-white rounded-[30px] shadow-[0px_3px_4px_0px_rgba(0,0,0,0.06)] outline outline-1 outline-offset-[-1px] flex justify-start items-center gap-5 ${
                      !input.isValid
                        ? "outline-red-500 shadow-red-200"
                        : "outline-neutral-200"
                    }`}
                  >
                    <div className="min-w-14 min-h-14 px-5 py-5 bg-blue-50 rounded-full outline outline-1 outline-offset-[-1px] outline-gray-50 inline-flex flex-col justify-center items-center">
                      <svg className="icon w-5 text-zinc-800">
                        <use href="#icon_globe"></use>
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={input.value}
                      onChange={(e) =>
                        handlePageInputChange(input.id, e.target.value)
                      }
                      onBlur={(e) => {
                        let val = e.target.value.trim();
                        // auto-add protocol if missing
                        if (!/^https?:\/\//i.test(val)) {
                          val = "https://" + val;
                        }
                        //auto remove trailing slash
                        if (val.endsWith("/")) {
                          val = val.slice(0, -1);
                        }
                        e.target.value = val;
                        // update input value and validate
                        handlePageInputChange(input.id, val);
                        validatePageInput(input.id, val);
                      }}
                      disabled={!isOwner}
                      className="text-sky-700 text-lg font-medium w-full outline-none border-0 ring-0 focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Enter page URL"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (isOwner) addMorePageInput();
                        }
                      }}
                    />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isOwner) removePageInput(input.id);
                    }}
                    disabled={pageInputs.length === 1 || !isOwner}
                    className={`${
                      pageInputs.length === 1 || !isOwner
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content={
                      !isOwner
                        ? "Only workspace owner can perform this action"
                        : ""
                    }
                    data-tooltip-place="top"
                  >
                    <svg className="icon text-[22px] text-[#DF3939] ms-4">
                      <use href="#icon_script_delete"></use>
                    </svg>
                  </button>
                </div>
                {!input.isValid && input.errorMessage && (
                  <p className="text-red-500 text-sm ml-20">
                    {input.errorMessage}
                  </p>
                )}
              </div>
            ))}

            {/* {specificPages.length > 0 && (
              <div className="mt-6">
                <h6 className="text-sm font-medium text-gray-600 mb-2">
                  Saved Pages:
                </h6>
                <ul className="space-y-2">
                  {specificPages.map((page, idx) => (
                    <li
                      key={idx}
                      className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded-lg"
                    >
                      <span className="text-sm">{page}</span>
                      <button
                        type="button"
                        className="text-[#DF3939] text-sm hover:underline"
                        onClick={() => {
                          setSpecificPages((prev) =>
                            prev.filter((_, i) => i !== idx)
                          );
                        }}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )} */}
          </div>
        </ModalBody>
        <ModalFooter className="border-0">
          <div className="flex items-center justify-end w-full">
            <button
              type="button"
              className="py-2 max-w-[180px] w-full me-6 rounded-[20px] font-semibold text-[18px] ring-[1px] ring-[#2A2A2A] ring-inset hover:ring-[none] bg-gradient-to-r hover:from-[#5E78F4] hover:to-[#F14CE2] hover:text-white hover:transition hover:delay-75"
              onClick={() => {
                setOpenSpecificPagesModal(false);
                setPageInputs([
                  { id: 1, value: "", isValid: true, errorMessage: "" },
                ]);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`py-2 max-w-[180px] w-full rounded-[20px] font-semibold text-[18px] ring-[1px] ring-[#2A2A2A] ring-inset hover:ring-[none] bg-gradient-to-r hover:from-[#5E78F4] hover:to-[#F14CE2] hover:text-white hover:transition hover:delay-75 flex items-center justify-center ${
                !isAllInputsValid() || !isOwner
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={saveSpecificPages}
              disabled={!isAllInputsValid() || isSavingPages || !isOwner}
            >
              {isSavingPages ? <Loader /> : "Save"}
            </button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Popup */}
      <Modal
        show={deleteConfirmModal.open}
        onClose={() => setDeleteConfirmModal({ open: false, inputId: null })}
        size="sm"
        className="delete-confirm-popup"
      >
        <div className="bg-white rounded-md p-8 text-center">
          <div className="mb-4">
            <svg
              className="w-6 h-6 text-red-500 mx-auto"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
            </svg>
          </div>
          <h3 className="text-[20px] font-semibold text-[#2A2A2A] mb-2">
            Are You Sure ?
          </h3>
          <p className="text-[16px] text-[#2A2A2A] mb-6">
            Do You Really Want To Delete
            <br />
            This Record
          </p>
          <div className="flex gap-5 justify-center">
            <button
              onClick={() =>
                setDeleteConfirmModal({ open: false, inputId: null })
              }
              className="px-8 py-2 border border-[#2A2A2A] rounded-full text-[14px] font-medium text-[#2A2A2A] hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={confirmRemovePageInput}
              className="px-8 py-2 border border-[#2A2A2A] rounded-full text-[14px] font-medium text-[#2A2A2A] hover:bg-gray-50"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Track URL Confirmation Popup */}
      <Modal
        show={deleteTrackUrlConfirmModal.open}
        onClose={() => setDeleteTrackUrlConfirmModal({ open: false, url: "" })}
        size="sm"
        className="delete-confirm-popup"
      >
        <div className="bg-white rounded-md p-8 text-center">
          <div className="mb-4">
            <svg
              className="w-6 h-6 text-red-500 mx-auto"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
            </svg>
          </div>
          <h3 className="text-[20px] font-semibold text-[#2A2A2A] mb-2">
            Are You Sure?
          </h3>
          <p className="text-[16px] text-[#2A2A2A] mb-6">
            Do you really want to delete this page URL?
          </p>
          <div className="flex gap-5 justify-center">
            <button
              onClick={() =>
                setDeleteTrackUrlConfirmModal({ open: false, url: "" })
              }
              className="px-8 py-2 border border-[#2A2A2A] rounded-full text-[14px] font-medium text-[#2A2A2A] hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={confirmDeleteTrackUrl}
              disabled={isDeletingTrackUrl}
              className="px-8 py-2 border border-[#2A2A2A] rounded-full text-[14px] font-medium text-[#2A2A2A] hover:bg-gray-50 flex items-center justify-center"
            >
              {isDeletingTrackUrl ? <Loader /> : "Delete"}
            </button>
          </div>
        </div>
      </Modal>

      <TestScriptInfoPopup
        scriptCheckInfoPopup={scriptCheckInfoPopup}
        setscriptCheckInfoPopup={setscriptCheckInfoPopup}
        scriptCheckFn={scriptCheckFn}
      />

      <SuccessModal
        open={openSuccessModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
      <FailureModal
        open={openFailureModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
      <DeletePopup
        openModal={openModalDelete}
        onClose={closeModalHandlerDelete}
        deleteFile={deleteFile}
        deleting={deleting}
        isKnowledgeBase={domainList?.is_knowledgebase}
      />
      <TooltipOld
        id="my-tooltip"
        // style={{ backgroundColor: "#7b7b7b", color: "white" }}
      />
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        setIsOpen={setIsUpgradeModalOpen}
      />
    </>
  );
}

export default Script;
